import moment from 'moment';

/**
 * Centralized date formatting utility for consistent date display across the application
 * Uses human-readable, non-technical formats
 */

// Standard date format: "January 5th, 2025 at 3:30 PM"
const STANDARD_FORMAT = 'MMMM Do, YYYY [at] h:mm A';

// Short date format for tables: "Jan 5, 2025"
const SHORT_FORMAT = 'MMM D, YYYY';

// Date with time for lists: "January 5, 2025 at 3:30 PM" - more readable than comma
const LIST_FORMAT = 'MMMM D, YYYY [at] h:mm A';

// Time only: "3:30 PM"
const TIME_ONLY_FORMAT = 'h:mm A';

// Full date without time: "January 5th, 2025"
const DATE_ONLY_FORMAT = 'MMMM Do, YYYY';

// Relative time format (e.g., "2 hours ago", "in 3 days")
const getRelativeTime = (date) => {
  return moment(date).fromNow();
};

/**
 * Format a date/timestamp into a human-readable string
 * @param {string|number|Date} date - The date to format (can be Unix timestamp, ISO string, Date object, etc.)
 * @param {string} format - The format type: 'standard', 'short', 'list', 'time', 'date', 'relative'
 * @param {boolean} isUnixMs - Whether the input is a Unix timestamp in milliseconds (default: false)
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'standard', isUnixMs = false) => {
  if (!date) return '-';
  
  let momentDate;
  
  // Handle different input types
  if (typeof date === 'number') {
    // Unix timestamp - check if it's in seconds or milliseconds
    if (isUnixMs || date > 9999999999) {
      momentDate = moment(date);
    } else {
      momentDate = moment.unix(date);
    }
  } else if (typeof date === 'string') {
    // Check for special AWS timestamp format "YYYY-MM-DD-HH:mm:ss.SSS"
    if (date.match(/^\d{4}-\d{2}-\d{2}-\d{2}:\d{2}:\d{2}\.\d{3}$/)) {
      momentDate = moment(date, "YYYY-MM-DD-HH:mm:ss.SSS");
    } else {
      momentDate = moment(date);
    }
  } else {
    momentDate = moment(date);
  }
  
  // Return appropriate format
  switch (format) {
    case 'short':
      return momentDate.format(SHORT_FORMAT);
    case 'list':
      return momentDate.format(LIST_FORMAT);
    case 'time':
      return momentDate.format(TIME_ONLY_FORMAT);
    case 'date':
      return momentDate.format(DATE_ONLY_FORMAT);
    case 'relative':
      return getRelativeTime(momentDate);
    case 'standard':
    default:
      return momentDate.format(STANDARD_FORMAT);
  }
};

/**
 * Format a date for display with intelligent format selection
 * Shows relative time for recent dates, full format for older dates
 * @param {string|number|Date} date - The date to format
 * @param {boolean} isUnixMs - Whether the input is a Unix timestamp in milliseconds
 * @returns {string} Formatted date string
 */
export const formatDateSmart = (date, isUnixMs = false) => {
  if (!date) return '-';
  
  const momentDate = typeof date === 'number' && !isUnixMs ? moment.unix(date) : moment(date);
  const now = moment();
  const diffHours = now.diff(momentDate, 'hours');
  const diffDays = now.diff(momentDate, 'days');
  
  // Less than 24 hours: show relative time
  if (Math.abs(diffHours) < 24) {
    return getRelativeTime(momentDate);
  }
  // Less than 7 days: show day and time
  else if (Math.abs(diffDays) < 7) {
    return momentDate.format('dddd [at] h:mm A');
  }
  // This year: don't show year
  else if (momentDate.year() === now.year()) {
    return momentDate.format('MMMM Do [at] h:mm A');
  }
  // Different year: show full date
  else {
    return momentDate.format(STANDARD_FORMAT);
  }
};

// Vue filter functions for backwards compatibility
export const dateFilters = {
  moment: (date) => formatDate(date, 'list'),
  momentFromUnix: (date) => formatDate(date, 'list', false),
  momentShort: (date) => formatDate(date, 'short'),
  momentDate: (date) => formatDate(date, 'date'),
  momentTime: (date) => formatDate(date, 'time'),
  momentRelative: (date) => formatDate(date, 'relative'),
  momentSmart: (date) => formatDateSmart(date)
};

export default {
  formatDate,
  formatDateSmart,
  filters: dateFilters
};